import type {
  SpreadsheetCellAddress,
  SpreadsheetRangeAddress,
  SpreadsheetValidationDropdownOptionsResponsePayload,
  SpreadsheetValidationErrorStyle,
  SpreadsheetValidationIssue,
  SpreadsheetValidationRule,
} from '../protocol/spreadsheetEngineProtocol'
import { formatCellAddress, formatSelectionAddress, type SpreadsheetSelectionRange } from './addressing'
import { colToLetter } from '../utils/columnUtils'
import { describeCellProtection } from './cellMetadataPresentation'

export const VALIDATION_ALERT_DEFAULT_TITLES: Record<SpreadsheetValidationErrorStyle, string> = {
  stop: 'Invalid data',
  warning: 'Warning',
  information: 'Information',
}

export const VALIDATION_ALERT_NOTICE_STYLES: Record<SpreadsheetValidationErrorStyle, {
  border: string
  background: string
  color: string
  icon: string
}> = {
  stop: {
    border: 'rgba(185, 28, 28, 0.38)',
    background: '#fff7f7',
    color: '#7f1d1d',
    icon: '!',
  },
  warning: {
    border: 'rgba(180, 83, 9, 0.44)',
    background: '#fffbeb',
    color: '#78350f',
    icon: '!',
  },
  information: {
    border: 'rgba(16, 124, 65, 0.35)',
    background: '#f0fdf4',
    color: 'var(--fg-green)',
    icon: 'i',
  },
}

export type ValidationInputPrompt = {
  title: string
  message: string
}

export type ValidationInvalidEntryActionId = 'accept-anyway' | 'retry' | 'cancel' | 'help'
export type InvalidEntryNoticeSource = 'validation-mutation' | 'validation-scan' | 'validation-status'

export type ValidationInvalidEntryAction = {
  id: ValidationInvalidEntryActionId
  label: string
  title: string
}

export type ProtectedInvalidEntryActionId = 'edit-protection' | 'sheet-protection' | 'cancel'

export type InvalidEntryNoticeAction = ValidationInvalidEntryAction | {
  id: ProtectedInvalidEntryActionId
  label: string
  title: string
}

export type InvalidEntryNotice = {
  kind?: 'validation' | 'protected'
  source?: InvalidEntryNoticeSource
  col?: number
  row?: number
  targetRange?: SpreadsheetSelectionRange
  operationLabel?: string
  offendingCellRange?: SpreadsheetSelectionRange
  title?: string
  message: string
  style?: SpreadsheetValidationErrorStyle
  attemptedValue?: string
  canOverride?: boolean
  retryWithValidationOverride?: () => void
  protectionReason?: string
}

export type PositionedInvalidEntryNotice = InvalidEntryNotice & {
  x: number
  y: number
}

export type InvalidEntryNoticePresentation = {
  alertStyle: SpreadsheetValidationErrorStyle
  noticeStyle: typeof VALIDATION_ALERT_NOTICE_STYLES[SpreadsheetValidationErrorStyle]
  title: string
  message: string
  isProtected: boolean
  target: string | null
  targetLabel: 'Range' | 'Cell'
  offendingCell: string | null
  showsSeparateOffendingCell: boolean
  hasAttemptedValue: boolean
  attemptedValueLabel: string | null
  operationLabel: string | null
  protectionReason: string | null
  actions: InvalidEntryNoticeAction[]
}

export type InvalidEntryActionPlan =
  | {
    action: 'accept-override'
    col: number
    row: number
    attemptedValue: string | null
    retryWithValidationOverride: (() => void) | null
  }
  | {
    action: 'retry-edit'
    selection: SpreadsheetSelectionRange
    scrollTo: { col: number; row: number }
    editValue: string | null
  }
  | {
    action: 'open-validation-settings'
    selection: SpreadsheetSelectionRange
  }
  | {
    action: 'edit-cell-protection'
    selection: SpreadsheetSelectionRange
    scrollTo: { col: number; row: number }
  }
  | { action: 'open-sheet-protection' }
  | { action: 'dismiss' }

export type InvalidEntryWorkflowEffect =
  | {
    kind: 'apply-validation-override'
    col: number
    row: number
    attemptedValue: string | null
    retryWithValidationOverride: (() => void) | null
  }
  | { kind: 'set-selection'; selection: SpreadsheetSelectionRange }
  | { kind: 'notify-selection-change' }
  | { kind: 'scroll-to-cell'; col: number; row: number }
  | { kind: 'dismiss-notice' }
  | { kind: 'close-validation-dropdown' }
  | { kind: 'refresh-validation-rules' }
  | { kind: 'open-cell-metadata-mode'; mode: 'validation' | 'protection' }
  | { kind: 'open-sheet-protection' }
  | { kind: 'start-editing-deferred'; col: number; row: number; value: string }
  | { kind: 'request-render' }
  | { kind: 'workbook-chrome-view-state-change' }

export type InvalidEntryActionWorkflowPlan = {
  action: InvalidEntryActionPlan['action']
  effects: InvalidEntryWorkflowEffect[]
}

export type ValidationSelectionRange = {
  col: number
  row: number
  endCol: number
  endRow: number
}

export type ValidationScreenRect = {
  x: number
  y: number
  w: number
  h: number
}

export type ValidationScreenPoint = {
  x: number
  y: number
}

export type ValidationViewport = {
  width: number
  height: number
}

export type ValidationVisibleBounds = {
  startCol: number
  endCol: number
  startRow: number
  endRow: number
}

export type ValidationDropdownProjection = {
  col: number
  row: number
  values: string[]
  sourceFormula: string
  sourceRanges: SpreadsheetRangeAddress[]
  currentValue: string
  error: string | null
  x: number
  y: number
}

export type ValidationDropdownFetchPlan = {
  shouldClearOptions: boolean
  request: SpreadsheetCellAddress | null
}

export type ValidationDropdownButtonPresentation = {
  ariaLabel: string
  title: string
  glyph: string
}

export type ValidationListPickerOptionPresentation = {
  value: string
  label: string
  title: string
  isCurrent: boolean
  isFocused: boolean
  currentLabel: string | null
}

export type ValidationListPickerPresentation = {
  selectionLabel: string
  dialogLabel: string
  dialogTitle: string
  closeLabel: string
  closeTitle: string
  filterVisible: boolean
  filterLabel: string
  filterTitle: string
  filterPlaceholder: string
  listboxLabel: string
  hasValues: boolean
  filteredValues: string[]
  options: ValidationListPickerOptionPresentation[]
  emptyMessage: string | null
  statusText: string
  statusTitle: string
  statusIsError: boolean
  editRuleLabel: string
  editRuleTitle: string
}

export type ValidationPromptProjection = {
  x: number
  y: number
  title: string
  message: string
}

export type ValidationIssueMarkerProjection = {
  key: string
  title: string
  ariaLabel: string
  x: number
  y: number
  width: number
  height: number
}

export type ValidationScanIssueSelection = {
  issue: SpreadsheetValidationIssue
  col: number
  row: number
}

export type ValidationScanWorkflowPlan =
  | {
    action: 'show-notice'
    issues: SpreadsheetValidationIssue[]
    notice: PositionedInvalidEntryNotice
  }
  | {
    action: 'focus-issue'
    issues: SpreadsheetValidationIssue[]
    selectedIssue: SpreadsheetValidationIssue
    selection: SpreadsheetSelectionRange
  }

export type ValidationCommandId = 'data-validation' | 'circle-invalid-data' | 'clear-validation-circles'

export type ValidationCommandPresentation = Record<ValidationCommandId, {
  enabled: boolean
  disabledReason: string
}>

export type ValidationContextMenuEntry = {
  commandId: 'data-validation'
  action: 'data-validation'
}

export type ValidationMutationErrorInput = {
  requestId?: string | null
  message?: string | null
  col?: number | null
  row?: number | null
  targetRange?: SpreadsheetRangeAddress | null
  operationLabel?: string | null
  validationIssue?: SpreadsheetValidationIssue | null
  protectedCell?: SpreadsheetCellAddress | null
  value?: string | null
  retryWithValidationOverride?: (() => void) | null
}

export type ValidationCellMetadataLike = {
  validationId?: string | null
  protection?: Record<string, unknown> | null
}

export function selectValidationRuleForMetadata(args: {
  metadata?: ValidationCellMetadataLike | null
  validationRules?: SpreadsheetValidationRule[] | null
}): SpreadsheetValidationRule | null {
  const validationId = String(args.metadata?.validationId ?? '').trim()
  if (!validationId) return null
  return (args.validationRules ?? []).find((rule) => rule.id === validationId) ?? null
}

export function normalizeValidationAlertStyle(style?: string | null): SpreadsheetValidationErrorStyle {
  return style === 'warning' || style === 'information' ? style : 'stop'
}

export function validationAlertTitle(
  style: SpreadsheetValidationErrorStyle,
  title?: string | null,
  fallback = VALIDATION_ALERT_DEFAULT_TITLES[style],
) {
  const trimmed = String(title ?? '').trim()
  return trimmed || fallback
}

export function formatValidationSourceRange(range: SpreadsheetRangeAddress): string {
  const startRow = Math.min(range.start_row, range.end_row)
  const endRow = Math.max(range.start_row, range.end_row)
  const startCol = Math.min(range.start_col, range.end_col)
  const endCol = Math.max(range.start_col, range.end_col)
  const start = `${colToLetter(startCol)}${startRow + 1}`
  const end = `${colToLetter(endCol)}${endRow + 1}`
  const sheetPrefix = String(range.sheet_id ?? '').trim() ? `${range.sheet_id}!` : ''
  return start === end ? `${sheetPrefix}${start}` : `${sheetPrefix}${start}:${end}`
}

export function validationRangeAddressToSelection(range?: SpreadsheetRangeAddress | null): SpreadsheetSelectionRange | undefined {
  if (!range) return undefined
  const startCol = Math.max(0, Math.floor(Number(range.start_col) || 0))
  const startRow = Math.max(0, Math.floor(Number(range.start_row) || 0))
  const endCol = Math.max(startCol, Math.floor(Number(range.end_col) || startCol))
  const endRow = Math.max(startRow, Math.floor(Number(range.end_row) || startRow))
  return {
    col: startCol,
    row: startRow,
    endCol,
    endRow,
  }
}

function positionNoticeNearCell(args: {
  cellRect?: ValidationScreenRect | null
  viewport?: ValidationViewport | null
  width: number
  height: number
}): { x: number; y: number } {
  const rect = args.cellRect
  const viewport = args.viewport
  if (!rect || !viewport) return { x: 16, y: 16 }
  const maxX = Math.max(8, viewport.width - args.width)
  const maxY = Math.max(8, viewport.height - args.height)
  return {
    x: Math.max(8, Math.min(maxX, rect.x + rect.w + 8)),
    y: Math.max(8, Math.min(maxY, rect.y)),
  }
}

function positionNoticeNearPoint(args: {
  point?: ValidationScreenPoint | null
  viewport?: ValidationViewport | null
  width: number
  height: number
}): { x: number; y: number } {
  const point = args.point
  const viewport = args.viewport
  if (!point || !viewport) return { x: 16, y: 16 }
  const maxX = Math.max(8, viewport.width - args.width)
  const maxY = Math.max(8, viewport.height - args.height)
  return {
    x: Math.max(8, Math.min(maxX, point.x + 8)),
    y: Math.max(8, Math.min(maxY, point.y + 8)),
  }
}

export function normalizeValidationListPickerValues(values?: unknown[] | null): string[] {
  return Array.from(new Set((values ?? [])
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)))
}

export function getValidationListPickerFilteredValues(args: {
  values?: unknown[] | null
  filter?: string | null
}): string[] {
  const normalizedValues = normalizeValidationListPickerValues(args.values)
  const query = String(args.filter ?? '').trim().toLowerCase()
  if (!query) return normalizedValues
  return normalizedValues.filter((value) => value.toLowerCase().includes(query))
}

export function getValidationListPickerDefaultFocusedIndex(args: {
  values?: unknown[] | null
  currentValue?: unknown
  filter?: string | null
}): number {
  const filteredValues = getValidationListPickerFilteredValues({
    values: args.values,
    filter: args.filter,
  })
  const currentValue = String(args.currentValue ?? '').trim()
  const currentIndex = filteredValues.findIndex((value) => value === currentValue)
  return Math.max(0, currentIndex)
}

export function buildValidationListPickerPresentation(args: {
  selectionLabel: string
  values?: unknown[] | null
  currentValue?: unknown
  sourceFormula?: string | null
  sourceRanges?: SpreadsheetRangeAddress[] | null
  error?: string | null
  filter?: string | null
  focusedIndex?: number
}): ValidationListPickerPresentation {
  const selectionLabel = String(args.selectionLabel || 'selected cell').trim() || 'selected cell'
  const currentValue = String(args.currentValue ?? '').trim()
  const values = normalizeValidationListPickerValues(args.values)
  const filteredValues = getValidationListPickerFilteredValues({
    values,
    filter: args.filter,
  })
  const hasValues = values.length > 0
  const focusedIndex = Math.max(0, Math.min(Math.max(filteredValues.length - 1, 0), args.focusedIndex ?? 0))
  const sourceFormula = String(args.sourceFormula ?? '').trim()
  const sourceRangeText = (args.sourceRanges ?? []).map(formatValidationSourceRange).join(', ')
  const error = String(args.error ?? '').trim()
  const statusText = error
    ? `Source error: ${error}`
    : sourceFormula
      ? `Source: ${sourceFormula}${sourceRangeText ? ` · ${sourceRangeText}` : ''}`
      : hasValues
        ? `${filteredValues.length} of ${values.length} values`
        : 'No list values available'

  return {
    selectionLabel,
    dialogLabel: `Data validation list for ${selectionLabel}`,
    dialogTitle: `Data validation list for ${selectionLabel}`,
    closeLabel: 'Close data validation list',
    closeTitle: 'Close data validation list',
    filterVisible: values.length > 6,
    filterLabel: 'Filter data validation values',
    filterTitle: 'Filter data validation values',
    filterPlaceholder: 'Search list',
    listboxLabel: `Allowed values for ${selectionLabel}`,
    hasValues,
    filteredValues,
    options: filteredValues.map((value, index) => {
      const isCurrent = value === currentValue
      return {
        value,
        label: value,
        title: isCurrent ? `${value} is the current value` : `Set ${selectionLabel} to ${value}`,
        isCurrent,
        isFocused: index === focusedIndex,
        currentLabel: isCurrent ? 'Current' : null,
      }
    }),
    emptyMessage: filteredValues.length > 0 ? null : hasValues ? 'No matching values.' : statusText,
    statusText,
    statusTitle: statusText,
    statusIsError: Boolean(error),
    editRuleLabel: 'Edit',
    editRuleTitle: 'Edit Data Validation',
  }
}

export function getValidationInputPrompt(rule?: SpreadsheetValidationRule | null): ValidationInputPrompt | null {
  const title = String(rule?.input_title ?? '').trim()
  const message = String(rule?.input_message ?? '').trim()
  if (!title && !message) return null
  return {
    title: title || 'Input Message',
    message,
  }
}

export function getValidationInvalidEntryActions(args: {
  style?: SpreadsheetValidationErrorStyle | string | null
  canOverride?: boolean
  hasAttemptedValue?: boolean
  hasCellTarget?: boolean
}): ValidationInvalidEntryAction[] {
  const style = normalizeValidationAlertStyle(args.style)
  const canRetry = Boolean(args.hasAttemptedValue && args.hasCellTarget)
  const actions: ValidationInvalidEntryAction[] = []
  const canOverride = Boolean(style !== 'stop' && args.canOverride && args.hasAttemptedValue && args.hasCellTarget)
  if (canOverride) {
    actions.push({
      id: 'accept-anyway',
      label: style === 'information' ? 'OK' : 'Yes',
      title: style === 'information'
        ? 'Accept this value despite the validation information message'
        : 'Accept this value despite the validation warning',
    })
  }
  if (canRetry && style !== 'information') {
    actions.push({
      id: 'retry',
      label: style === 'warning' && canOverride ? 'No' : 'Retry',
      title: style === 'warning' && canOverride
        ? 'Return to editing the rejected value'
        : 'Edit the rejected value again',
    })
  }
  actions.push({
    id: 'cancel',
    label: 'Cancel',
    title: 'Keep the previous cell value',
  })
  if (args.hasCellTarget) {
    actions.push({
      id: 'help',
      label: 'Help',
      title: 'Open the Data Validation settings for this cell',
    })
  }
  return actions
}

export function buildInvalidEntryNoticePresentation(args: {
  notice?: InvalidEntryNotice | null
  sheetSize?: { colCount?: number; rowCount?: number }
}): InvalidEntryNoticePresentation | null {
  const notice = args.notice
  if (!notice) return null
  const alertStyle = normalizeValidationAlertStyle(notice.style)
  const isProtected = notice.kind === 'protected'
  const targetRange = notice.targetRange
  const target = targetRange
    ? formatSelectionAddress(targetRange, args.sheetSize)
    : notice.col != null && notice.row != null
      ? formatCellAddress(notice.col, notice.row)
      : null
  const offendingCell = notice.offendingCellRange
    ? formatSelectionAddress(notice.offendingCellRange, args.sheetSize)
    : null
  const showsSeparateOffendingCell = Boolean(offendingCell && target && offendingCell !== target)
  const hasCellTarget = notice.col != null && notice.row != null
  const hasAttemptedValue = notice.attemptedValue != null || notice.retryWithValidationOverride != null
  const actions: InvalidEntryNoticeAction[] = isProtected
    ? [
      ...(hasCellTarget ? [{
        id: 'edit-protection' as const,
        label: 'Edit Cell Protection',
        title: 'Open protection settings for this cell',
      }] : []),
      {
        id: 'sheet-protection',
        label: 'Protect Sheet',
        title: 'Open sheet protection settings',
      },
      {
        id: 'cancel',
        label: 'OK',
        title: 'Dismiss protected-cell message',
      },
    ]
    : getValidationInvalidEntryActions({
      style: alertStyle,
      canOverride: notice.canOverride,
      hasAttemptedValue,
      hasCellTarget,
    })

  return {
    alertStyle,
    noticeStyle: VALIDATION_ALERT_NOTICE_STYLES[alertStyle],
    title: notice.title ?? validationAlertTitle(alertStyle),
    message: notice.message,
    isProtected,
    target,
    targetLabel: targetRange && (targetRange.col !== targetRange.endCol || targetRange.row !== targetRange.endRow) ? 'Range' : 'Cell',
    offendingCell,
    showsSeparateOffendingCell,
    hasAttemptedValue,
    attemptedValueLabel: notice.attemptedValue != null ? notice.attemptedValue || '(blank)' : null,
    operationLabel: notice.operationLabel ?? null,
    protectionReason: notice.protectionReason ?? null,
    actions,
  }
}

function noticeCellSelection(notice?: InvalidEntryNotice | null): SpreadsheetSelectionRange | null {
  if (notice?.col == null || notice.row == null) return null
  return {
    col: notice.col,
    row: notice.row,
    endCol: notice.col,
    endRow: notice.row,
  }
}

function noticeRetrySelection(notice?: InvalidEntryNotice | null): SpreadsheetSelectionRange | null {
  const cellSelection = noticeCellSelection(notice)
  if (!cellSelection) return null
  return notice?.targetRange ?? cellSelection
}

export function buildInvalidEntryActionPlan(args: {
  notice?: InvalidEntryNotice | null
  actionId?: string | null
}): InvalidEntryActionPlan {
  const notice = args.notice ?? null
  const actionId = String(args.actionId ?? '').trim()
  const cellSelection = noticeCellSelection(notice)
  switch (actionId) {
    case 'accept-anyway':
      if (
        notice?.canOverride &&
        notice.col != null &&
        notice.row != null &&
        (notice.retryWithValidationOverride || notice.attemptedValue != null)
      ) {
        return {
          action: 'accept-override',
          col: notice.col,
          row: notice.row,
          attemptedValue: notice.attemptedValue ?? null,
          retryWithValidationOverride: notice.retryWithValidationOverride ?? null,
        }
      }
      return { action: 'dismiss' }
    case 'retry': {
      const retrySelection = noticeRetrySelection(notice)
      if (!retrySelection || !cellSelection) return { action: 'dismiss' }
      return {
        action: 'retry-edit',
        selection: retrySelection,
        scrollTo: { col: cellSelection.col, row: cellSelection.row },
        editValue: notice?.attemptedValue ?? null,
      }
    }
    case 'help':
      if (!cellSelection) return { action: 'dismiss' }
      return {
        action: 'open-validation-settings',
        selection: cellSelection,
      }
    case 'edit-protection':
      if (!cellSelection) return { action: 'dismiss' }
      return {
        action: 'edit-cell-protection',
        selection: cellSelection,
        scrollTo: { col: cellSelection.col, row: cellSelection.row },
      }
    case 'sheet-protection':
      return { action: 'open-sheet-protection' }
    case 'cancel':
    default:
      return { action: 'dismiss' }
  }
}

export function buildInvalidEntryActionWorkflowPlan(args: {
  notice?: InvalidEntryNotice | null
  actionId?: string | null
}): InvalidEntryActionWorkflowPlan {
  const plan = buildInvalidEntryActionPlan(args)
  switch (plan.action) {
    case 'accept-override':
      return {
        action: plan.action,
        effects: [
          {
            kind: 'apply-validation-override',
            col: plan.col,
            row: plan.row,
            attemptedValue: plan.attemptedValue,
            retryWithValidationOverride: plan.retryWithValidationOverride,
          },
          { kind: 'dismiss-notice' },
          { kind: 'workbook-chrome-view-state-change' },
        ],
      }
    case 'retry-edit':
      return {
        action: plan.action,
        effects: [
          { kind: 'set-selection', selection: plan.selection },
          { kind: 'notify-selection-change' },
          { kind: 'scroll-to-cell', col: plan.scrollTo.col, row: plan.scrollTo.row },
          { kind: 'dismiss-notice' },
          ...(plan.editValue != null
            ? [{ kind: 'start-editing-deferred' as const, col: plan.scrollTo.col, row: plan.scrollTo.row, value: plan.editValue }]
            : []),
          { kind: 'workbook-chrome-view-state-change' },
        ],
      }
    case 'open-validation-settings':
      return {
        action: plan.action,
        effects: [
          { kind: 'set-selection', selection: plan.selection },
          { kind: 'notify-selection-change' },
          { kind: 'dismiss-notice' },
          { kind: 'close-validation-dropdown' },
          { kind: 'refresh-validation-rules' },
          { kind: 'open-cell-metadata-mode', mode: 'validation' },
          { kind: 'workbook-chrome-view-state-change' },
        ],
      }
    case 'edit-cell-protection':
      return {
        action: plan.action,
        effects: [
          { kind: 'set-selection', selection: plan.selection },
          { kind: 'notify-selection-change' },
          { kind: 'scroll-to-cell', col: plan.scrollTo.col, row: plan.scrollTo.row },
          { kind: 'dismiss-notice' },
          { kind: 'close-validation-dropdown' },
          { kind: 'open-cell-metadata-mode', mode: 'protection' },
          { kind: 'workbook-chrome-view-state-change' },
        ],
      }
    case 'open-sheet-protection':
      return {
        action: plan.action,
        effects: [
          { kind: 'dismiss-notice' },
          { kind: 'close-validation-dropdown' },
          { kind: 'open-sheet-protection' },
          { kind: 'workbook-chrome-view-state-change' },
        ],
      }
    case 'dismiss':
    default:
      return {
        action: 'dismiss',
        effects: [
          { kind: 'dismiss-notice' },
          { kind: 'request-render' },
        ],
      }
  }
}

export function buildInvalidEntryNoticeFromMutationError(args: {
  error: ValidationMutationErrorInput
  validationRules?: SpreadsheetValidationRule[] | null
  activeSheetName?: string | null
  activeSheetProtected?: boolean
  getCellMetadata?: (col: number, row: number) => ValidationCellMetadataLike | null | undefined
  getCellRect?: (col: number, row: number) => ValidationScreenRect | null | undefined
  viewport?: ValidationViewport | null
}): PositionedInvalidEntryNotice {
  const { error } = args
  const validationIssue = error.validationIssue ?? null
  const protectedCell = error.protectedCell ?? null
  const isProtectedEdit = Boolean(protectedCell && !validationIssue)
  const message = String(
    validationIssue?.message
    || error.message
    || 'The edit was rejected by the spreadsheet engine.',
  ).trim()
  const targetRange = validationRangeAddressToSelection(error.targetRange)
  const col = Number.isFinite(Number(protectedCell?.col))
    ? Math.max(0, Math.floor(Number(protectedCell?.col)))
    : Number.isFinite(Number(validationIssue?.address?.col))
      ? Math.max(0, Math.floor(Number(validationIssue?.address?.col)))
      : Number.isFinite(Number(error.col))
        ? Math.max(0, Math.floor(Number(error.col)))
        : targetRange?.col
  const row = Number.isFinite(Number(protectedCell?.row))
    ? Math.max(0, Math.floor(Number(protectedCell?.row)))
    : Number.isFinite(Number(validationIssue?.address?.row))
      ? Math.max(0, Math.floor(Number(validationIssue?.address?.row)))
      : Number.isFinite(Number(error.row))
        ? Math.max(0, Math.floor(Number(error.row)))
        : targetRange?.row
  if (col == null || row == null) {
    return {
      kind: isProtectedEdit ? 'protected' : 'validation',
      x: 16,
      y: 16,
      title: isProtectedEdit ? 'Protected cell' : 'Invalid entry',
      message,
      operationLabel: error.operationLabel ?? undefined,
      protectionReason: isProtectedEdit
        ? 'The spreadsheet engine rejected this edit before changing worksheet data.'
        : undefined,
    }
  }

  const metadata = args.getCellMetadata?.(col, row) ?? null
  const validationId = validationIssue?.validation_id?.trim() || metadata?.validationId?.trim()
  const rule = validationIssue?.rule ?? (validationId
    ? (args.validationRules ?? []).find((candidate) => candidate.id === validationId)
    : null)
  const alertStyle = normalizeValidationAlertStyle(validationIssue?.error_style ?? rule?.error_style)
  const attemptedValue = String(validationIssue?.raw_input ?? error.value ?? '')
  const retryWithValidationOverride = error.retryWithValidationOverride ?? undefined
  const canOverride = Boolean(rule && alertStyle !== 'stop' && (attemptedValue || retryWithValidationOverride))
  const cellProtectionLabel = describeCellProtection(metadata?.protection ?? null)
  const activeSheetName = String(args.activeSheetName ?? '').trim() || 'the active sheet'
  const protectionReason = isProtectedEdit
    ? cellProtectionLabel === 'Locked cell'
      ? `Cell ${colToLetter(col)}${row + 1} has an explicit locked-cell protection rule.`
      : args.activeSheetProtected
        ? `Sheet "${activeSheetName}" is protected; cells are locked by default unless they are explicitly unlocked.`
        : 'The spreadsheet engine reported this cell as protected and rejected the edit before changing worksheet data.'
    : undefined
  const position = positionNoticeNearCell({
    cellRect: args.getCellRect?.(col, row),
    viewport: args.viewport,
    width: 280,
    height: 96,
  })
  return {
    kind: isProtectedEdit ? 'protected' : 'validation',
    source: 'validation-mutation',
    col,
    row,
    targetRange: targetRange ?? { col, row, endCol: col, endRow: row },
    offendingCellRange: { col, row, endCol: col, endRow: row },
    operationLabel: error.operationLabel ?? undefined,
    style: alertStyle,
    title: isProtectedEdit
      ? 'Protected cell'
      : validationAlertTitle(alertStyle, validationIssue?.error_title ?? rule?.error_title, 'Invalid entry'),
    message,
    attemptedValue: attemptedValue || undefined,
    canOverride,
    retryWithValidationOverride,
    protectionReason,
    ...position,
  }
}

export function buildValidationStatusNotice(args: {
  title?: string | null
  message: string
  kind?: 'validation' | 'protected'
  source?: InvalidEntryNoticeSource
  operationLabel?: string | null
  protectionReason?: string | null
}): PositionedInvalidEntryNotice {
  return {
    kind: args.kind ?? 'validation',
    source: args.source ?? 'validation-status',
    x: 16,
    y: 16,
    title: String(args.title ?? '').trim() || 'Invalid data',
    message: args.message,
    operationLabel: args.operationLabel ?? undefined,
    protectionReason: args.protectionReason ?? undefined,
  }
}

export function buildProtectedSheetActionNotice(args: {
  message: string
  operationLabel?: string | null
  sheetName?: string | null
  pointer?: ValidationScreenPoint | null
  viewport?: ValidationViewport | null
}): PositionedInvalidEntryNotice {
  const sheetName = String(args.sheetName ?? '').trim() || 'the active sheet'
  return {
    kind: 'protected',
    title: 'Protected sheet',
    message: args.message,
    operationLabel: args.operationLabel ?? undefined,
    protectionReason: `Sheet "${sheetName}" is protected; this action is disabled by the sheet protection options.`,
    ...positionNoticeNearPoint({
      point: args.pointer,
      viewport: args.viewport,
      width: 280,
      height: 96,
    }),
  }
}

export function selectValidationScanIssue(args: {
  issues?: SpreadsheetValidationIssue[] | null
  activeSheetId?: string | null
}): ValidationScanIssueSelection | null {
  const issues = args.issues ?? []
  const issue = issues.find((candidate) => candidate.address.sheet_id === args.activeSheetId) ?? issues[0] ?? null
  if (!issue) return null
  return {
    issue,
    col: Math.max(0, Math.floor(Number(issue.address.col) || 0)),
    row: Math.max(0, Math.floor(Number(issue.address.row) || 0)),
  }
}

export function buildValidationScanResultNotice(args: {
  issues?: SpreadsheetValidationIssue[] | null
  activeSheetId?: string | null
  selectedIssue?: SpreadsheetValidationIssue | null
  cellRect?: ValidationScreenRect | null
  viewport?: ValidationViewport | null
}): PositionedInvalidEntryNotice {
  const issues = args.issues ?? []
  const selection = args.selectedIssue
    ? {
      issue: args.selectedIssue,
      col: Math.max(0, Math.floor(Number(args.selectedIssue.address.col) || 0)),
      row: Math.max(0, Math.floor(Number(args.selectedIssue.address.row) || 0)),
    }
    : selectValidationScanIssue({ issues, activeSheetId: args.activeSheetId })
  if (!selection) {
    return buildValidationStatusNotice({
      title: 'Invalid data',
      source: 'validation-scan',
      message: 'No invalid data found on this sheet.',
    })
  }
  const { issue, col, row } = selection
  const issueCount = issues.length
  const alertStyle = normalizeValidationAlertStyle(issue.error_style ?? issue.rule?.error_style)
  const position = positionNoticeNearCell({
    cellRect: args.cellRect,
    viewport: args.viewport,
    width: 300,
    height: 108,
  })
  return {
    kind: 'validation',
    source: 'validation-scan',
    col,
    row,
    style: alertStyle,
    title: validationAlertTitle(alertStyle, issue.error_title ?? issue.rule?.error_title),
    message: `${issueCount} invalid cell${issueCount === 1 ? '' : 's'} found. ${issue.message}`,
    ...position,
  }
}

export function buildValidationScanWorkflowPlan(args: {
  issues?: SpreadsheetValidationIssue[] | null
  activeSheetId?: string | null
}): ValidationScanWorkflowPlan {
  const issues = args.issues ?? []
  const selectedIssue = selectValidationScanIssue({
    issues,
    activeSheetId: args.activeSheetId,
  })
  if (!selectedIssue) {
    return {
      action: 'show-notice',
      issues,
      notice: buildValidationScanResultNotice({
        issues,
        activeSheetId: args.activeSheetId,
      }),
    }
  }
  const selection = {
    col: selectedIssue.col,
    row: selectedIssue.row,
    endCol: selectedIssue.col,
    endRow: selectedIssue.row,
  }
  return {
    action: 'focus-issue',
    issues,
    selectedIssue: selectedIssue.issue,
    selection,
  }
}

export function shouldClearValidationScanNotice(notice?: InvalidEntryNotice | null): boolean {
  return notice?.source === 'validation-scan'
}

export function buildValidationCommandPresentation(args: {
  hasSelection?: boolean | null
  hasStreamer?: boolean | null
  validationIssueCount?: number | null
}): ValidationCommandPresentation {
  const hasSelection = Boolean(args.hasSelection)
  const hasStreamer = Boolean(args.hasStreamer)
  const validationIssueCount = Math.max(0, Math.floor(Number(args.validationIssueCount) || 0))
  return {
    'data-validation': {
      enabled: hasSelection && hasStreamer,
      disabledReason: 'Select cells after the spreadsheet engine is ready',
    },
    'circle-invalid-data': {
      enabled: hasStreamer,
      disabledReason: 'Spreadsheet engine is not ready',
    },
    'clear-validation-circles': {
      enabled: validationIssueCount > 0,
      disabledReason: 'No validation circles to clear',
    },
  }
}

export function buildValidationContextMenuEntries(): ValidationContextMenuEntry[] {
  return [{
    commandId: 'data-validation',
    action: 'data-validation',
  }]
}

export function buildValidationDropdownProjection(args: {
  disabled?: boolean
  rule?: SpreadsheetValidationRule | null
  projectedOptions?: SpreadsheetValidationDropdownOptionsResponsePayload | null
  activeSheetId?: string | null
  selection?: ValidationSelectionRange | null
  cellRect?: ValidationScreenRect | null
  viewport?: ValidationViewport | null
  currentValue?: unknown
}): ValidationDropdownProjection | null {
  const { rule, selection, cellRect, viewport } = args
  if (args.disabled || rule?.kind !== 'list' || !selection || !cellRect || !viewport) return null
  const col = Math.min(selection.col, selection.endCol)
  const row = Math.min(selection.row, selection.endRow)
  const projectedOptions = args.projectedOptions
  const projectedOptionsMatch =
    projectedOptions != null &&
    projectedOptions.sheet_id === args.activeSheetId &&
    projectedOptions.address.row === row &&
    projectedOptions.address.col === col &&
    projectedOptions.validation_id === rule.id
  const values = (projectedOptionsMatch
    ? projectedOptions.options
    : (rule.values ?? []))
    .map((value) => String(value ?? '').trim())
    .filter(Boolean)
  const sourceFormula = String(
    projectedOptionsMatch
      ? projectedOptions.source_formula ?? rule.formula1 ?? ''
      : rule.formula1 ?? '',
  ).trim()
  return {
    col,
    row,
    values,
    sourceFormula,
    sourceRanges: projectedOptionsMatch ? projectedOptions.source_ranges ?? [] : [],
    currentValue: String(args.currentValue ?? '').trim(),
    error: projectedOptionsMatch ? projectedOptions.error ?? null : null,
    x: Math.max(4, Math.min(viewport.width - 28, cellRect.x + cellRect.w - 22)),
    y: Math.max(4, Math.min(viewport.height - 28, cellRect.y + 2)),
  }
}

export function buildValidationDropdownFetchPlan(args: {
  disabled?: boolean
  rule?: SpreadsheetValidationRule | null
  activeSheetId?: string | null
  selection?: ValidationSelectionRange | null
  hasStreamer?: boolean | null
}): ValidationDropdownFetchPlan {
  if (args.disabled || !args.hasStreamer || args.rule?.kind !== 'list' || !args.selection) {
    return {
      shouldClearOptions: true,
      request: null,
    }
  }
  const selection = args.selection
  return {
    shouldClearOptions: false,
    request: {
      sheet_id: String(args.activeSheetId ?? ''),
      row: Math.min(selection.row, selection.endRow),
      col: Math.min(selection.col, selection.endCol),
    },
  }
}

export function buildValidationDropdownButtonPresentation(
  projection?: ValidationDropdownProjection | null,
): ValidationDropdownButtonPresentation | null {
  if (!projection) return null
  const hasValues = projection.values.length > 0
  return {
    ariaLabel: 'Open data validation list',
    title: hasValues ? 'Open data validation list' : 'Open data validation source options',
    glyph: 'v',
  }
}

export function buildValidationPromptProjection(args: {
  disabled?: boolean
  prompt?: ValidationInputPrompt | null
  selection?: ValidationSelectionRange | null
  cellRect?: ValidationScreenRect | null
  viewport?: ValidationViewport | null
}): ValidationPromptProjection | null {
  const { prompt, selection, cellRect, viewport } = args
  if (args.disabled || !prompt || !selection || !cellRect || !viewport) return null
  const maxX = Math.max(8, viewport.width - 280)
  const maxY = Math.max(8, viewport.height - 120)
  return {
    x: Math.max(8, Math.min(maxX, cellRect.x + cellRect.w + 8)),
    y: Math.max(8, Math.min(maxY, cellRect.y)),
    title: prompt.title,
    message: prompt.message,
  }
}

export function buildValidationIssueMarkerProjections(args: {
  disabled?: boolean
  issues?: SpreadsheetValidationIssue[] | null
  activeSheetId?: string | null
  visibleBounds: ValidationVisibleBounds
  getCellRect: (col: number, row: number) => ValidationScreenRect | null | undefined
}): ValidationIssueMarkerProjection[] {
  const issues = args.issues ?? []
  if (args.disabled || issues.length === 0) return []
  return issues.flatMap((issue) => {
    if (issue.address.sheet_id !== args.activeSheetId) return []
    const col = Math.max(0, Math.floor(Number(issue.address.col) || 0))
    const row = Math.max(0, Math.floor(Number(issue.address.row) || 0))
    if (
      col < args.visibleBounds.startCol ||
      col > args.visibleBounds.endCol ||
      row < args.visibleBounds.startRow ||
      row > args.visibleBounds.endRow
    ) {
      return []
    }
    const rect = args.getCellRect(col, row)
    if (!rect) return []
    const alertStyle = normalizeValidationAlertStyle(issue.error_style ?? issue.rule?.error_style)
    const alertTitle = validationAlertTitle(alertStyle, issue.error_title ?? issue.rule?.error_title)
    const title = `${colToLetter(col)}${row + 1}: ${alertTitle}. ${issue.message}`
    return [{
      key: `${issue.validation_id}:${row}:${col}`,
      title,
      ariaLabel: title,
      x: rect.x + 3,
      y: rect.y + 3,
      width: Math.max(8, rect.w - 6),
      height: Math.max(8, rect.h - 6),
    }]
  })
}
